/**
 * Safari-specific storage utilities
 * Handles Safari Private Mode and ITP restrictions
 */

// In-memory fallback storage for Safari Private Mode
const memoryStorage: Record<string, string> = {};

/**
 * Detect if running in Safari browser
 */
export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
};

/**
 * Detect if in Safari Private Browsing Mode
 * Safari Private Mode throws QuotaExceededError on localStorage.setItem
 */
export const isSafariPrivateMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const testKey = '__safari_private_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return false;
  } catch (e) {
    return true;
  }
};

/**
 * Safe storage wrapper that falls back to memory storage in Safari Private Mode
 */
export const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage.getItem failed, using memory fallback:', key);
      return memoryStorage[key] || null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // QuotaExceededError in Safari Private Mode
      console.warn('localStorage.setItem failed, using memory fallback:', key);
      memoryStorage[key] = value;
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage.removeItem failed:', key);
    }
    delete memoryStorage[key];
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (e) {
      console.warn('localStorage.clear failed');
    }
    Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
  }
};

/**
 * Safe sessionStorage wrapper
 */
export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      console.warn('sessionStorage.getItem failed, using memory fallback:', key);
      return memoryStorage[`session_${key}`] || null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      console.warn('sessionStorage.setItem failed, using memory fallback:', key);
      memoryStorage[`session_${key}`] = value;
    }
  },

  removeItem: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('sessionStorage.removeItem failed:', key);
    }
    delete memoryStorage[`session_${key}`];
  },

  clear: (): void => {
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('sessionStorage.clear failed');
    }
    Object.keys(memoryStorage)
      .filter(key => key.startsWith('session_'))
      .forEach(key => delete memoryStorage[key]);
  }
};

/**
 * Check if storage is available and working
 */
export const isStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get a simple fallback fingerprint when canvas fails (Safari 17+ Private Mode)
 */
export const getSimpleFingerprint = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth?.toString() || '',
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || '',
    navigator.maxTouchPoints?.toString() || ''
  ].join('|');
  
  // Simple hash
  let hash = 0;
  for (let i = 0; i < components.length; i++) {
    const char = components.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString();
};
